import React, { useCallback, useEffect, useMemo, useState } from "react"
import { DATA_FOCUSABLE_CELL } from "./tableUtils"
import { SafeTypedTableRow, TableHeader } from "./types"

interface UseTableKeyboardNavigationParams {
  paginatedData: SafeTypedTableRow[]
  headers: TableHeader[]
  showGroupHeaders: boolean
  showCheckboxColumn: boolean
  showEditColumn: boolean
  showDeleteColumn: boolean
  isMobile: boolean
  tableCardHeaderGroupsEff: string[] | undefined
}

export function useTableKeyboardNavigation({
  paginatedData,
  headers,
  showGroupHeaders,
  showCheckboxColumn,
  showEditColumn,
  showDeleteColumn,
  isMobile,
  tableCardHeaderGroupsEff,
}: UseTableKeyboardNavigationParams) {
  const [focusedCell, setFocusedCell] = useState<{
    row: number
    col: number
    table?: number
  } | null>(null)

  const formatSelectorValueText = useCallback(
    (row: number, col: number, table?: number) =>
      [row, col, table].filter((val) => val !== undefined).join("-"),
    [],
  )

  const focusCellDesktop = useCallback(
    (row: number, col: number, table?: number) => {
      setFocusedCell({ row, col, table })
      const cellDataValue = formatSelectorValueText(row, col, table)
      setTimeout(() => {
        const cell = document.querySelector(
          `[${DATA_FOCUSABLE_CELL}*="${cellDataValue}"]`,
        ) as HTMLElement | null
        if (cell) cell.focus()
      }, 0)
    },
    [formatSelectorValueText],
  )

  const focusedCellFullDataValue = useMemo(() => {
    if (!focusedCell) return ""
    const { row, col, table } = focusedCell
    const partial = formatSelectorValueText(row, col, table)
    const cell = document.querySelector(
      `[${DATA_FOCUSABLE_CELL}*="${partial}"]`,
    ) as HTMLElement | null
    return cell?.getAttribute(DATA_FOCUSABLE_CELL) ?? partial
  }, [focusedCell, formatSelectorValueText])

  const isCellFocused = useCallback(
    (row: number, col: number, table?: number) => {
      const dataValueToCheck = formatSelectorValueText(row, col, table)
      return focusedCellFullDataValue.split(",").includes(dataValueToCheck)
    },
    [focusedCellFullDataValue, formatSelectorValueText],
  )

  const isAnyCellFocused = focusedCell !== null

  const handleTableKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!focusedCell) return

      const { row, col } = focusedCell
      const maxRow = paginatedData.length + (showGroupHeaders ? 1 : 0)
      const maxCol =
        headers.length -
        1 +
        (showCheckboxColumn ? 1 : 0) +
        (showEditColumn ? 1 : 0) +
        (showDeleteColumn ? 1 : 0)
      let nextRow = row
      let nextCol = col

      switch (e.key) {
        case "ArrowRight": {
          const spanArray = focusedCellFullDataValue.split(",")
          const spanIdx = spanArray.findIndex((v) => v === `${row}-${col}`)
          const skip = spanArray.length - spanIdx - 1
          if (col + skip + 1 <= maxCol) nextCol = col + skip + 1
          break
        }
        case "ArrowLeft": {
          const spanArray = focusedCellFullDataValue.split(",")
          const spanIdx = spanArray.findIndex((v) => v === `${row}-${col}`)
          if (col - spanIdx - 1 >= 0) nextCol = col - spanIdx - 1
          break
        }
        case "ArrowDown":
          if (row < maxRow) nextRow = row + 1
          break
        case "ArrowUp":
          if (row > 0) nextRow = row - 1
          break
        case "Home":
          if (e.ctrlKey) {
            nextRow = 0
            nextCol = 0
          } else {
            nextCol = 0
          }
          break
        case "End":
          if (e.ctrlKey) {
            nextRow = maxRow
            nextCol = maxCol
          } else {
            nextCol = maxCol
          }
          break
        case "PageDown":
          nextRow = Math.min(row + 5, maxRow)
          break
        case "PageUp":
          nextRow = Math.max(row - 5, 0)
          break
        default:
          return
      }
      e.preventDefault()
      focusCellDesktop(nextRow, nextCol)
    },
    [
      focusedCell,
      paginatedData.length,
      showGroupHeaders,
      headers.length,
      showCheckboxColumn,
      showEditColumn,
      showDeleteColumn,
      focusCellDesktop,
      focusedCellFullDataValue,
    ],
  )

  const handleMobileTableKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!focusedCell) return

      const { row, col, table } = focusedCell
      const trimmedHeaderGroups: string[] = []
      // Remove leading empties
      for (let i = 0; i < (tableCardHeaderGroupsEff ?? []).length; i++) {
        if (tableCardHeaderGroupsEff![i] !== "") {
          trimmedHeaderGroups.push(...tableCardHeaderGroupsEff!.slice(i))
          break
        }
      }

      // Remove consecutive dupes
      const dedupedHeaderGroups = trimmedHeaderGroups.filter(
        (group, index) =>
          index === 0 || group !== trimmedHeaderGroups[index - 1],
      )

      const maxRow =
        headers.length +
        (tableCardHeaderGroupsEff ? dedupedHeaderGroups.length : 0) +
        (showEditColumn ? 1 : 0) +
        (showDeleteColumn ? 1 : 0) -
        1
      const maxCol = 1
      const maxTable = paginatedData.length - 1
      let nextRow = row
      let nextCol = col
      let nextTable = table

      switch (e.key) {
        case "ArrowRight":
          if (col < maxCol) nextCol = col + 1
          break
        case "ArrowLeft":
          if (col > 0) nextCol = col - 1
          break
        case "ArrowDown":
          if (row < maxRow) {
            nextRow = row + 1
          } else if (row === maxRow && (table ?? 0) < maxTable) {
            nextRow = 0
            nextTable = (table ?? 0) + 1
          }
          break
        case "ArrowUp":
          if (row > 0) {
            nextRow = row - 1
          } else if (row === 0 && (table ?? 0) > 0) {
            nextRow = maxRow
            nextTable = (table ?? 0) - 1
          }
          break
        case "Home":
          if (e.ctrlKey) {
            nextRow = 0
            nextCol = 0
            nextTable = 0
          } else {
            nextCol = 0
          }
          break
        case "End":
          if (e.ctrlKey) {
            nextRow = maxRow
            nextCol = maxCol
            nextTable = maxTable
          } else {
            nextCol = maxCol
          }
          break
        case "PageDown":
          if ((table ?? 0) < maxTable) {
            nextRow = 0
            nextTable = (table ?? 0) + 1
          }
          break
        case "PageUp":
          if ((table ?? 0) > 0) {
            nextRow = 0
            nextTable = (table ?? 0) - 1
          }
          break
        default:
          return
      }
      e.preventDefault()
      focusCellDesktop(nextRow, nextCol, nextTable)
    },
    [
      focusedCell,
      tableCardHeaderGroupsEff,
      headers,
      showEditColumn,
      showDeleteColumn,
      paginatedData.length,
      focusCellDesktop,
    ],
  )

  // Imperatively focus the DOM element when focusedCell changes
  useEffect(() => {
    if (!focusedCell) return
    const key = [focusedCell.row, focusedCell.col, focusedCell.table]
      .filter((part) => part !== undefined)
      .join("-")
    const cell = document.querySelector(
      `[${DATA_FOCUSABLE_CELL}*="${key}"]`,
    ) as HTMLElement | null
    cell?.focus()
  }, [focusedCell])

  // Reset focused cell when crossing the mobile/desktop breakpoint
  useEffect(() => {
    setFocusedCell(null)
  }, [isMobile])

  return {
    focusedCell,
    setFocusedCell,
    focusCellDesktop,
    isCellFocused,
    isAnyCellFocused,
    handleTableKeyDown,
    handleMobileTableKeyDown,
  }
}
